import { useCallback, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { getApiUrl } from '../config';

export interface ChatMessage {
  id: string;
  groupId: string;
  content: string | null;
  deleted: boolean;
  createdAt: string;
  sender: { id: string; name: string; avatar?: string | null } | null;
}

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('authToken')}`,
  'Content-Type': 'application/json',
});

function socketUrl(): string {
  if (process.env.NODE_ENV === 'development') return ''; // proxy
  return process.env.REACT_APP_API_URL || '';
}

export function useGroupChat(groupId: string | undefined, enabled: boolean) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const socketRef = useRef<Socket | null>(null);

  // Carga inicial / paginación hacia atrás.
  const loadInitial = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    try {
      const res = await fetch(getApiUrl(`/api/groups/${groupId}/messages?limit=30`), { headers: authHeaders() });
      const data = await res.json().catch(() => ({ messages: [] }));
      setMessages(Array.isArray(data.messages) ? data.messages : []);
      setNextCursor(data.nextCursor || null);
      setHasMore(!!data.nextCursor);
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  const loadOlder = useCallback(async () => {
    if (!groupId || !nextCursor) return;
    const res = await fetch(getApiUrl(`/api/groups/${groupId}/messages?limit=30&before=${encodeURIComponent(nextCursor)}`), { headers: authHeaders() });
    const data = await res.json().catch(() => ({ messages: [] }));
    setMessages((prev) => [...(data.messages || []), ...prev]);
    setNextCursor(data.nextCursor || null);
    setHasMore(!!data.nextCursor);
  }, [groupId, nextCursor]);

  const send = useCallback(async (content: string) => {
    if (!groupId || !content.trim() || sending) return;
    setSending(true);
    try {
      const res = await fetch(getApiUrl(`/api/groups/${groupId}/messages`), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ content: content.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.message) {
        // El socket también lo emite; dedupe por id al recibirlo.
        setMessages((prev) => (prev.some((m) => m.id === data.message.id) ? prev : [...prev, data.message]));
      }
    } finally {
      setSending(false);
    }
  }, [groupId, sending]);

  const remove = useCallback(async (messageId: string) => {
    if (!groupId) return;
    await fetch(getApiUrl(`/api/groups/${groupId}/messages/${messageId}`), { method: 'DELETE', headers: authHeaders() });
    setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, deleted: true, content: null } : m)));
  }, [groupId]);

  const markRead = useCallback(async () => {
    if (!groupId || messages.length === 0) return;
    const last = messages[messages.length - 1];
    await fetch(getApiUrl(`/api/groups/${groupId}/read`), {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ lastReadMsgId: last.id }),
    }).catch(() => {});
  }, [groupId, messages]);

  // Socket: unirse a la sala del grupo y escuchar en vivo, solo si el chat está abierto.
  useEffect(() => {
    if (!enabled || !groupId) return;
    loadInitial();

    const s = io(socketUrl(), {
      transports: ['websocket', 'polling'],
      forceNew: true,
      auth: { token: localStorage.getItem('authToken') },
    });
    socketRef.current = s;
    s.on('connect', () => s.emit('joinGroup', groupId));
    s.on('groupMessage', (evt: any) => {
      const m = evt?.message;
      if (m && m.groupId === groupId) {
        setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
      }
    });
    s.on('groupMessageDeleted', (evt: any) => {
      const id = evt?.messageId;
      if (id) setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, deleted: true, content: null } : m)));
    });

    return () => {
      try { s.emit('leaveGroup', groupId); } catch {}
      s.disconnect();
      socketRef.current = null;
    };
  }, [enabled, groupId, loadInitial]);

  return { messages, loading, sending, hasMore, loadOlder, send, remove, markRead, reload: loadInitial };
}
